import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw.length < 6) return setErr("Password must be at least 6 characters.");
        if (pw !== pw2) return setErr("Passwords do not match.");
        if (!token) return setErr("Invalid or missing reset token in the URL.");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
            <div className="flex flex-col p-8 sm:p-12 lg:p-16">
                <Link to="/" className="flex items-center gap-2.5 mb-12 w-fit">
                    <div className="h-8 w-8 bg-brand text-white grid place-items-center font-heading font-bold tracking-tight rounded-sm">A</div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-slate-900 text-base">Allied Pod</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Barbados</div>
                    </div>
                </Link>
                <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Account recovery</p>
                    <h1 className="font-heading text-4xl tracking-tight font-bold text-slate-900">Set a new password.</h1>
                    {!done ? (
                        <>
                            <p className="text-sm text-slate-600 mt-3 leading-relaxed">Choose a password you have not used elsewhere. The link expires 1 hour after it was issued.</p>
                            <form onSubmit={submit} className="mt-10 space-y-5">
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">New password</Label>
                                    <Input
                                        type="password"
                                        required
                                        minLength={6}
                                        value={pw}
                                        onChange={(e) => setPw(e.target.value)}
                                        data-testid="reset-pw-input"
                                        className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                        placeholder="At least 6 characters"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Confirm password</Label>
                                    <Input
                                        type="password"
                                        required
                                        value={pw2}
                                        onChange={(e) => setPw2(e.target.value)}
                                        data-testid="reset-pw2-input"
                                        className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                    />
                                </div>
                                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{err}</div>}
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="reset-submit-btn"
                                    className="w-full h-11 bg-brand hover:bg-brand-hover text-white rounded-md group"
                                >
                                    {loading ? "Updating…" : (
                                        <>
                                            Update password
                                            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div className="mt-8 p-6 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="reset-success">
                            <div className="flex items-start gap-3">
                                <div className="h-9 w-9 bg-emerald-100 text-emerald-700 grid place-items-center rounded-sm shrink-0">
                                    <ShieldCheck className="h-4 w-4" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <p className="font-heading font-semibold text-slate-900">Password updated.</p>
                                    <p className="text-sm text-slate-600 mt-1.5">Redirecting to sign in…</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="hidden lg:block relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1760611656615-db3fad24a314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBwb2R8ZW58MHx8fHwxNzc4MDExOTUwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Modern office space"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
            </div>
        </div>
    );
}
